import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  HostListener,
  QueryList,
  ViewChildren,
  ViewChild,
  contentChildren,
  effect,
  inject,
  input,
  output,
  signal,
} from '@angular/core';
import { PopoverMenuItemComponent } from './popover-menu-item.component';

/**
 * Dropdown / kebab menu. Compose with `<app-popover-menu-item>` children. The trigger
 * is projected via the `[trigger]` slot; click it (or press Enter/Space while focused)
 * to open the menu.
 *
 * Keyboard:
 * - Arrow Up / Down moves focus between enabled items
 * - Home / End jump to first / last
 * - Enter or Space activates the focused item
 * - Escape closes and returns focus to the trigger
 * - Tab out of the menu closes it
 *
 * @example
 * <app-popover-menu>
 *   <button trigger appTooltip="Options" aria-label="Options">…</button>
 *   <app-popover-menu-item (select)="edit()">Edit</app-popover-menu-item>
 *   <app-popover-menu-item (select)="duplicate()">Duplicate</app-popover-menu-item>
 *   <app-popover-menu-item (select)="delete()" destructive>Delete</app-popover-menu-item>
 * </app-popover-menu>
 *
 * @feature shared
 */
@Component({
  selector: 'app-popover-menu',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: { class: 'inline-block relative' },
  template: `
    <div class="inline-block" (click)="toggle()" (keydown)="onTriggerKey($event)">
      <ng-content select="[trigger]" />
    </div>

    @if (open()) {
      <div
        #menu
        role="menu"
        [attr.aria-label]="ariaLabel()"
        class="absolute z-40 mt-1 min-w-[10rem] rounded-lg border border-outline bg-surface shadow-[var(--theme-shadow)] py-1"
        [class.right-0]="align() === 'end'"
        [class.left-0]="align() === 'start'"
        (keydown)="onMenuKey($event)"
      >
        <ng-content />
      </div>
    }
  `,
})
export class PopoverMenuComponent implements AfterViewInit {
  private readonly host = inject(ElementRef<HTMLElement>);

  /** Accessible label for the menu when the trigger is icon-only. */
  readonly ariaLabel = input<string | undefined>(undefined);

  /** Which edge the menu aligns to. @default 'start' */
  readonly align = input<'start' | 'end'>('start');

  /** Fired when the menu opens. */
  readonly opened = output<void>();
  /** Fired when the menu closes. */
  readonly closed = output<void>();

  readonly open = signal(false);

  readonly items = contentChildren(PopoverMenuItemComponent);

  @ViewChild('menu') private menuEl?: ElementRef<HTMLDivElement>;
  @ViewChildren('menu') private _menus?: QueryList<ElementRef<HTMLDivElement>>;

  constructor() {
    effect(() => {
      const isOpen = this.open();
      if (isOpen) {
        this.opened.emit();
        queueMicrotask(() => this.focusFirst());
      } else {
        this.closed.emit();
      }
    });
  }

  ngAfterViewInit(): void {}

  toggle(): void {
    this.open.update(v => !v);
  }

  close(returnFocus = true): void {
    if (!this.open()) return;
    this.open.set(false);
    if (returnFocus) {
      const trigger = this.host.nativeElement.querySelector('[trigger]') as HTMLElement | null;
      trigger?.focus();
    }
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    if (!this.open()) return;
    if (!this.host.nativeElement.contains(event.target as Node)) {
      this.close(false);
    }
  }

  onTriggerKey(event: KeyboardEvent): void {
    if (event.key === 'Enter' || event.key === ' ' || event.key === 'ArrowDown') {
      event.preventDefault();
      if (!this.open()) this.open.set(true);
      else this.focusFirst();
    }
  }

  onMenuKey(event: KeyboardEvent): void {
    switch (event.key) {
      case 'Escape':
        event.preventDefault();
        this.close();
        return;
      case 'ArrowDown':
        event.preventDefault();
        this.moveFocus(1);
        return;
      case 'ArrowUp':
        event.preventDefault();
        this.moveFocus(-1);
        return;
      case 'Home':
        event.preventDefault();
        this.focusAt(0);
        return;
      case 'End':
        event.preventDefault();
        this.focusAt(this.items().length - 1);
        return;
      case 'Tab':
        this.close(false);
        return;
    }
  }

  /** Called by a child item when it is selected. */
  onItemSelected(): void {
    this.close();
  }

  private focusFirst(): void {
    this.focusAt(0);
  }

  private focusAt(index: number): void {
    const els = this.menuButtons();
    if (!els.length) return;
    const clamped = Math.max(0, Math.min(index, els.length - 1));
    els[clamped].focus();
  }

  private moveFocus(step: number): void {
    const els = this.menuButtons();
    if (!els.length) return;
    const current = els.findIndex(b => b === document.activeElement);
    const next = (current + step + els.length) % els.length;
    els[next].focus();
  }

  private menuButtons(): HTMLButtonElement[] {
    const root = this.menuEl?.nativeElement;
    if (!root) return [];
    return Array.from(root.querySelectorAll<HTMLButtonElement>('button[role="menuitem"]:not([disabled])'));
  }
}
